import type { ImagePreviewModalProps } from './index.types';
import { FloatingPortal } from '@floating-ui/react';
import CancelIcon from 'Assets/icons/cancel.svg';
import DownloadIcon from 'Assets/icons/download.svg';
import NoImageIcon from 'Assets/icons/no-image.svg';
import ZoomLessIcon from 'Assets/icons/zoom-less.svg';
import ZoomMoreIcon from 'Assets/icons/zoom-more.svg';
import { parseMessageDate } from 'Components/Chat/utils/parseMessageDate';
import { UserAvatar } from 'Components/shared/UserAvatar';
import { motion } from 'framer-motion';
import React, { useCallback } from 'react';
import FocusLock from 'react-focus-lock';
import { Loader } from 'semantic-ui-react';

import { pushToGTMDataLayer } from 'Utils/analytics';
import { Button } from '~/lib/ui/button';
import { MIN_SCALE } from './index.constants';
import { useImagePreviewModal } from './useImagePreviewModal';
import { truncateText } from './utils';

export const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({
  isOpen,
  onClose,
  image,
  imageUrl,
  isLoadingImage,
  senderName,
  messageDate,
  onDownload,
}) => {
  const {
    overlayRef,
    containerRef,
    imageRef,
    x,
    y,
    controls,
    isZoomed,
    dragConstraints,
    zoomIn,
    zoomOut,
    handleDoubleClick,
    handleDragEnd,
  } = useImagePreviewModal(isOpen, onClose);

  const [isDragging, setIsDragging] = React.useState(false);
  const [imageLoadError, setImageLoadError] = React.useState(false);

  React.useEffect(() => {
    if (isOpen) {
      if (!imageUrl && !isLoadingImage) {
        setImageLoadError(true);
      } else {
        setImageLoadError(false);
      }
    }
  }, [isOpen, imageUrl, isLoadingImage]);

  const handleImageError = () => {
    setImageLoadError(true);
  };

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();

    if (isDragging) {
      return;
    }
    if (event.target === overlayRef.current) {
      onClose();
    }
  };

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleDragEndWithTracking = () => {
    handleDragEnd();
    setTimeout(() => {
      setIsDragging(false);
    }, 0);
  };

  const handleImageContainerClick = (
    event: React.MouseEvent<HTMLDivElement>
  ) => {
    if (event.currentTarget === event.target) {
      onClose();
    }
  };

  const handleBarClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.currentTarget === event.target) {
      onClose();
    }
  };

  const handleImageClick = (event: React.MouseEvent<HTMLImageElement>) => {
    if (isDragging) {
      return;
    }
    handleDoubleClick(event);
  };

  const handleDownload = useCallback(async () => {
    if (!image) return;

    try {
      await onDownload(image.externalId);
      pushToGTMDataLayer('chat_image_preview_download');
    } catch (error) {
      console.error('Error downloading image:', error);
    }
  }, [image, onDownload]);

  if (!isOpen || !image) {
    return null;
  }

  const truncatedSenderName = truncateText(senderName, 34);
  const truncatedFileName = truncateText(image.description || 'Image', 84);
  const formattedDate = parseMessageDate(messageDate);

  return (
    <FloatingPortal>
      <FocusLock returnFocus autoFocus={false}>
        <div
          ref={overlayRef}
          onClick={handleOverlayClick}
          role="dialog"
          aria-modal="true"
          aria-label="Image preview"
          className="fixed top-0 left-0 w-screen h-screen m-0 p-0 bg-[#333333e6] z-image-preview-modal flex flex-col items-center justify-center overflow-hidden"
        >
          <div
            onClick={handleBarClick}
            className="absolute top-0 left-0 right-0 h-24 flex items-center justify-between p-[24px] z-image-preview-modal-header"
          >
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 [&_.list-item-icon-wrapper]:!bg-[#e6e6e6] [&_.list-item-icon-wrapper]:!border-[#e6e6e6] [&_.credentials]:font-semibold [&_.credentials]:!text-[#333]">
                <UserAvatar name={senderName} size={50} fontSize={20} />
              </div>

              <div className="flex flex-col gap-1 text-[14px] font-semibold">
                <p
                  className="text-white text-base whitespace-nowrap overflow-hidden text-ellipsis max-w-[300px] m-0"
                  style={{ fontFamily: 'Open Sans' }}
                >
                  {truncatedSenderName}
                </p>
                <p
                  className="text-white whitespace-nowrap m-0"
                  style={{ fontFamily: 'Open Sans' }}
                >
                  {formattedDate}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-6">
              <Button
                size="icon"
                variant="ghost"
                className="!w-8 !h-8 hover:bg-background/0"
                onClick={() => {
                  void handleDownload();
                }}
                disabled={isLoadingImage}
                data-testid="image-preview-download"
                aria-label="Download image"
                title="Download image"
              >
                <img
                  src={DownloadIcon}
                  alt="Download"
                  className="w-8 h-8 brightness-0 invert"
                />
              </Button>

              <Button
                size="icon"
                variant="ghost"
                className="!w-8 !h-8 hover:bg-background/0"
                onClick={zoomOut}
                disabled={isLoadingImage}
                data-testid="image-preview-zoom-out"
                aria-label="Zoom out"
                title="Zoom out"
              >
                <img
                  src={ZoomLessIcon}
                  alt="Zoom out"
                  className="w-8 h-8 brightness-0 invert"
                />
              </Button>

              <Button
                size="icon"
                variant="ghost"
                className="!w-8 !h-8 hover:bg-background/0"
                onClick={zoomIn}
                disabled={isLoadingImage}
                data-testid="image-preview-zoom-in"
                aria-label="Zoom in"
                title="Zoom in"
              >
                <img
                  src={ZoomMoreIcon}
                  alt="Zoom in"
                  className="w-8 h-8 brightness-0 invert"
                />
              </Button>

              <div className="w-[1.5px] h-[30px] bg-white mx-2 flex-shrink-0" />

              <Button
                size="icon"
                variant="ghost"
                className="!w-8 !h-8 hover:bg-background/0"
                onClick={onClose}
                data-testid="image-preview-close"
                aria-label="Close preview"
                title="Close preview"
              >
                <img
                  src={CancelIcon}
                  alt="Close"
                  className="w-8 h-8 brightness-0 invert"
                />
              </Button>
            </div>
          </div>

          <div
            ref={containerRef}
            data-zoomed={isZoomed}
            onClick={handleImageContainerClick}
            className="absolute top-[100px] left-0 right-0 bottom-20 w-full h-[calc(100vh-180px)] flex items-center justify-center overflow-hidden cursor-default touch-none"
          >
            {isLoadingImage ? (
              <Loader active inverted size="massive" />
            ) : imageLoadError ? (
              <div className="flex flex-col items-center justify-center gap-9 text-white">
                <img src={NoImageIcon} className="w-[121px] h-[121px]" />

                <p className="m-0 font-semibold text-[14px] leading-normal not-italic font-sans">
                  {"Sorry, we couldn't load the preview. Give it another try?"}
                </p>
              </div>
            ) : (
              <motion.img
                ref={imageRef}
                src={imageUrl}
                alt={image?.description || 'Preview'}
                onError={handleImageError}
                draggable={false}
                animate={controls}
                initial={{ scale: MIN_SCALE, x: 0, y: 0 }}
                drag={isZoomed}
                dragConstraints={dragConstraints}
                dragElastic={0.1}
                dragMomentum={false}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEndWithTracking}
                onClick={handleImageClick}
                style={{
                  x,
                  y,
                  maxWidth: '100%',
                  maxHeight: 'calc(100vh - 180px)',
                  objectFit: 'contain',
                  userSelect: 'none',
                  cursor: isZoomed ? 'grab' : 'zoom-in',
                  touchAction: 'none',
                }}
                whileDrag={{ cursor: 'grabbing' }}
              />
            )}
          </div>

          <div
            onClick={handleBarClick}
            className="absolute bottom-0 left-0 right-0 h-20 flex items-center justify-center z-image-preview-modal-footer px-8"
          >
            <p
              className="text-white text-center m-0 text-[14px] font-semibold"
              style={{ fontFamily: 'Open Sans' }}
            >
              {truncatedFileName}
            </p>
          </div>
        </div>
      </FocusLock>
    </FloatingPortal>
  );
};
